import { useEffect, useRef, useState } from 'react';

function HighwayMap({ highwayList }) {
    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const activeMarkerRef = useRef(null);
    const markerClickRef = useRef(false);
    const roadviewContainerRef = useRef(null);
    const roadviewRef = useRef(null);
    const [showRoadview, setShowRoadview] = useState(false);

    useEffect(() => {
        if (!window.kakao || !window.kakao.maps) {
            console.error('카카오 지도 API가 로드되지 않았습니다.');
            return;
        }

        const container = mapContainerRef.current;

        const options = {
            center: new window.kakao.maps.LatLng(
                36.0,
                127.8
            ),
            level: 13
        };

        mapRef.current = new window.kakao.maps.Map(
            container,
            options
        );

        infoWindowRef.current =
            new window.kakao.maps.InfoWindow({
                zIndex: 1
            });

        window.kakao.maps.event.addListener(
            mapRef.current,
            'click',
            async (mouseEvent) => {
                // 마커 클릭으로 발생한 지도 클릭이면 무시
                if (markerClickRef.current) {
                    markerClickRef.current = false;

                    return;
                }

                activeMarkerRef.current = null;

                if (infoWindowRef.current) {
                    infoWindowRef.current.close();
                }

                const position = mouseEvent.latLng;
                const address = await getAddress(position);

                showMapInfoWindow(
                    position,
                    address
                );

                showRoadView(position);
            }
        );

        // 로드뷰 객체
        roadviewRef.current =
            new window.kakao.maps.Roadview(
                roadviewContainerRef.current
            );
    }, []);

    useEffect(() => {
        if (!mapRef.current) {
            return;
        }

        if (!Array.isArray(highwayList)) {
            return;
        }

        // 기존 마커 제거
        markersRef.current.forEach(
            marker => marker.setMap(null)
        );

        markersRef.current = [];

        if (highwayList.length === 0) {
            return;
        }

        const bounds = new window.kakao.maps.LatLngBounds();

        highwayList.forEach((item) => {
            if (
                item.lat == null ||
                item.lng == null
            ) {
                return;
            }

            const position =
                new window.kakao.maps.LatLng(
                    Number(item.lat),
                    Number(item.lng)
                );

            const marker =
                new window.kakao.maps.Marker({
                    position,
                    map: mapRef.current,
                    clickable: true
                });

            markersRef.current.push(marker);

            bounds.extend(position);

            // 마커 클릭
            window.kakao.maps.event.addListener(
                marker,
                'click',
                async () => {
                    markerClickRef.current = true;

                    // =====================================
                    // 같은 마커를 다시 클릭
                    // =====================================
                    if (activeMarkerRef.current === marker) {
                        infoWindowRef.current.close();
                        activeMarkerRef.current = null;
                        setShowRoadview(false);

                        return;
                    }

                    // =====================================
                    // 다른 마커 클릭
                    // =====================================
                    // 기존 InfoWindow 닫기
                    infoWindowRef.current.close();

                    // 현재 마커 저장
                    activeMarkerRef.current = marker;

                    // 주소 조회 중 표시
                    showInfoWindow(
                        marker,
                        item,
                        '주소를 조회 중입니다...'
                    );

                    // 주소 조회
                    const address = await getAddress(position);

                    // 주소 조회하는 동안
                    // 다른 마커를 클릭했다면 무시
                    if (activeMarkerRef.current !== marker) {
                        return;
                    }

                    // 주소 표시
                    showInfoWindow(
                        marker,
                        item,
                        address
                    );

                    // 로드뷰 표시
                    showRoadView(position);
                }
            );
        });

        // 지도 영역 조정
        if (highwayList.length === 1) {
            const item = highwayList[0];

            const position = new window.kakao.maps.LatLng(
                Number(item.lat),
                Number(item.lng)
            );

            mapRef.current.setCenter(position);

            // 숫자가 클수록 확대
            // 7보다 작게 하면 더 넓은 영역 표시
            mapRef.current.setLevel(8);

        } else {
            mapRef.current.setBounds(
                bounds,
                50,
                50,
                50,
                50
            );
        }
    }, [highwayList]);

    const showInfoWindow = (
        marker,
        item,
        address = ''
    ) => {
        const content = `
            <div style="
                padding:10px;
                min-width:240px;
                font-size:13px;
                line-height:1.6;
            ">
                <div style="
                    font-weight:bold;
                    font-size:15px;
                    margin-bottom:6px;
                ">
                    ${item.name || ''}
                </div>

                <div>
                    도로번호 :
                    ${item.road_no || ''}
                </div>

                <div>
                    거리 :
                    ${item.distance ?? ''}
                    km
                </div>

                <div>
                    주소 :
                    ${address || '주소 정보 없음'}
                </div>

                <div>
                    위도 :
                    ${item.lat ?? ''}
                </div>

                <div>
                    경도 :
                    ${item.lng ?? ''}
                </div>
            </div>
        `;

        infoWindowRef.current.setContent(
            content
        );

        infoWindowRef.current.open(
            mapRef.current,
            marker
        );
    };

    const showMapInfoWindow = (
        position,
        address
    ) => {
        const content = `
            <div style="
                padding:10px;
                min-width:240px;
                font-size:13px;
                line-height:1.6;
            ">
                <div style="
                    font-weight:bold;
                    font-size:15px;
                    margin-bottom:6px;
                ">
                    지도 선택 위치
                </div>

                <div>
                    주소 :
                    ${address || '주소 정보 없음'}
                </div>

                <div>
                    위도 :
                    ${position.getLat()}
                </div>

                <div>
                    경도 :
                    ${position.getLng()}
                </div>
            </div>
        `;

        infoWindowRef.current.setContent(
            content
        );

        infoWindowRef.current.setPosition(
            position
        );

        infoWindowRef.current.open(
            mapRef.current
        );
    };

    const showRoadView = (position) => {
        if (!roadviewRef.current) {
            return;
        }

        const roadviewClient = new window.kakao.maps.RoadviewClient();

        roadviewClient.getNearestPanoId(
            position,
            50,
            (panoId) => {

                if (!panoId) {

                    setShowRoadview(false);

                    alert(
                        '해당 위치의 로드뷰를 찾을 수 없습니다.'
                    );

                    return;
                }

                // 로드뷰 영역 표시
                setShowRoadview(true);

                // 로드뷰 파노라마 지정
                roadviewRef.current.setPanoId(
                    panoId,
                    position
                );
            }
        );
    };

    const getAddress = (position) => {
        return new Promise((resolve) => {
            const geocoder = new window.kakao.maps.services.Geocoder();

            geocoder.coord2Address(
                position.getLng(),
                position.getLat(),
                (result, status) => {
                    if (status === window.kakao.maps.services.Status.OK) {
                        if (result && result.length > 0) {
                            const address =
                                result[0].road_address
                                    ? result[0].road_address.address_name
                                    : result[0].address
                                        ? result[0].address.address_name
                                        : '';

                            resolve(address);

                        } else {
                            resolve('');
                        }

                    } else {
                        resolve('');
                    }
                }
            );
        });
    };

    return (
        <div
            style={{
                width: '100%',
                maxWidth: '1600px',
                boxSizing: 'border-box'
            }}
        >
            <div
                ref={mapContainerRef}
                style={{
                    width: '100%',
                    height: '600px',
                    boxSizing: 'border-box'
                }}
            />

            {showRoadview && (
                <h3
                    style={{
                        marginTop: '15px',
                        marginBottom: '10px'
                    }}
                >
                    로드뷰
                </h3>
            )}

            <div
                ref={roadviewContainerRef}
                style={{
                    width: '100%',
                    height: showRoadview ? '600px' : '0px',
                    marginTop: showRoadview ? '10px' : '0px',
                    border: showRoadview
                        ? '1px solid #ddd'
                        : 'none',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}
            />
        </div>
    );
}

export default HighwayMap;
